import PinList from "@/components/PinList";
import { AD_POSITION } from "@/constants/ads";
import { RENDERCARDTYPE } from "@/constants/renderCardType";
import { getAds, getRecommendPins } from "@/services";
import { getPinDetailModal } from "@/utils/getPinDetailModal";
import { useIsMaterialVip } from "@/utils/hooks/useVip";
import useRequest from "@/utils/useReq";
import { useSetState } from "ahooks";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
// 为你推荐
function PinRecommendPinList({
  pinId,
  isModal,
  fileId,
  similiarList,
  showSimilarList,
  customColumn,
  customColumnSize,
  relatedBoards, // 新增：同在画板数据
  currentBoard = null, // 新增：当前采集所在画板
  artRecommend = null, // 新增：AI灵感推荐数据（可选）
  searchWord = null, // 新增：搜索词（可选）
}) {
  // 会员判断：会员显示版权标识，非会员不显示
  const isVip = useIsMaterialVip();

  const [query, setQuery] = useSetState({
    page: 1,
    pinId: null,
  });
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [ads, setAds] = useState([]);
  const [scrollableTarget, setScrollableTarget] = useState(null);
  const [masonryKey, setMasonryKey] = useState(null);

  // 解决瀑布流容器大小判断，因为弹窗过程需要时间，延迟计算
  useEffect(() => {
    const timeout = setTimeout(() => {
      setMasonryKey(pinId);
    }, 400);

    return () => {
      clearTimeout(timeout);
    };
  }, [pinId]);

  useRequest(
    async () => {
      const { page, pinId } = query;

      if (!pinId) {
        return;
      }

      const [result, waterfallAd] = await Promise.all([
        getRecommendPins({
          pin_id: pinId,
          limit: 20,
          page,
        }),
        page > 1 && page % 2 === 1 // 第一页不展示广告，每40个插一
          ? getAds({
              positions: [AD_POSITION.PINDETAIL_WATERFALL_PER_PAGE],
              variables: {
                user_tag: ["pin"],
              },
            })
          : null,
      ]);

      const pins = result?.pins || [];

      if (waterfallAd?.[0] && pins.length > 0) {
        //追加到列表前
        pins.unshift({
          ...waterfallAd[0],
          pin_id: `recommend-${waterfallAd[0].material.id}-${page}`,
          type: "recommend",
        });
      }

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource(dataSource.concat(pins));
    },
    {
      refreshDeps: [query],
    }
  );

  useRequest(async () => {
    const result = await getAds({
      positions: [53, 57],
      file_id: fileId,
    });

    setAds(
      result?.reduce((result, item) => {
        if (item) {
          result.push({
            ...item,
            pin_id: `recommend-${item.material.id}`,
            type: "recommend",
          });
        } else {
          result.push(null);
        }
        return result;
      }, []) || []
    );
  });

  const onLoadMore = useCallback(() => {
    if (dataSource.length > 0) {
      setQuery({
        page: query.page + 1,
      });
    }
  }, [dataSource, query, setQuery]);

  useEffect(() => {
    if (pinId) {
      setHasMore(true);
      setDataSource([]);
      setQuery({
        page: 1,
        pinId,
      });
    }
  }, [pinId, setQuery]);

  useEffect(() => {
    if (isModal) {
      const modal = getPinDetailModal();
      if (modal) {
        setScrollableTarget(modal);
      }
    }
  }, [isModal]);

  const list = useMemo(() => {
    let list = [...dataSource];

    // 1. 先插入相似采集运营位
    if (showSimilarList && similiarList.data?.pins?.length) {
      list.unshift({ ...similiarList, type: RENDERCARDTYPE.similar, pinId });
    }

    // 2. 插入同在画板运营位
    if (
      relatedBoards &&
      relatedBoards.length >= 3 &&
      relatedBoards.length < 10
    ) {
      // 如果有「所在画板」，且不在列表第一位，则添加到第一位
      let boardsToShow = relatedBoards;
      if (
        currentBoard &&
        relatedBoards[0]?.board_id !== currentBoard.board_id
      ) {
        boardsToShow = [currentBoard, ...relatedBoards];
      }

      list.unshift({
        type: RENDERCARDTYPE.boardOperation,
        boards: boardsToShow,
        pinId: pinId,
        currentBoardId: currentBoard?.board_id, // 传递当前采集所在画板的ID，用于显示角标
        pin_id: `boardOperation-${pinId}`, // 添加pin_id避免被过滤
        mergeCol: 3, // 关键：告诉Masonry组件这个元素占用3列
      });
    }

    // 3. 插入AI灵感推荐运营位（确保在第一位）
    if (artRecommend && searchWord) {
      list.unshift(artRecommend);
    }

    // 4. 如果没有数据或广告，直接返回
    if (dataSource.length <= 0 || ads.length <= 0) {
      return list;
    }

    // 5. 插入广告运营位
    return [
      ...list.slice(0, 3),
      ...ads.slice(1, 2).filter((i) => i),
      ...list.slice(3),
    ];
  }, [
    dataSource,
    ads,
    similiarList,
    pinId,
    showSimilarList,
    relatedBoards,
    currentBoard,
    artRecommend,
    searchWord,
  ]);

  if (isModal && !scrollableTarget) {
    return null;
  }

  return (
    <>
      <PinList
        disableModal={!isModal}
        disableQueue={true}
        disableFile={true}
        dataSource={list}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        scrollableTarget={scrollableTarget}
        masonryKey={masonryKey}
        customColumnNum={customColumn}
        customColumnSize={customColumnSize}
        isModal={isModal}
        searchWord={searchWord}
        showMaterialTag={isVip}
      />
    </>
  );
}
export default memo(PinRecommendPinList);
